import {
  BarChart2,
  RefreshCw,
  TrendingUp,
  Store,
  Users,
  IndianRupee,
  Percent,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  CalendarDays,
  Search,
  BadgeCheck,
  Clock,
  Building2,
  Activity,
} from "lucide-react";
import { useAdminReport, TABS } from "../../hooks/useAdminReport";
import Pagination from "../../components/tables/Pagination";

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const fmtDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const num = (v) => (v ?? 0).toLocaleString("en-IN");

// ─── shared sub-components ───────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, sub, iconBg, textColor, border }) {
  return (
    <div className={`bg-white rounded-lg p-5 border ${border || "border-gray-200"} shadow-sm flex items-start space-x-4`}>
      <div className={`${iconBg} p-3 rounded-lg flex-shrink-0`}>
        <Icon className={`h-6 w-6 ${textColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium truncate">{label}</p>
        <p className={`text-2xl font-bold ${textColor} mt-0.5`}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
      {children}
    </h3>
  );
}

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
        active
          ? "bg-blue-600 text-white shadow-sm"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      {label}
    </button>
  );
}

function PaidBadge({ isPaid }) {
  return isPaid ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
      <BadgeCheck className="h-3.5 w-3.5" />
      Paid
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
      <XCircle className="h-3.5 w-3.5" />
      Unpaid
    </span>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({
  period, handlePeriodChange,
  dashboardData, commissionData,
  overviewLoading, overviewError,
  refreshOverview, PERIODS,
}) {
  const tx   = dashboardData?.transactions || {};
  const biz  = dashboardData?.businesses   || {};
  const cust = dashboardData?.customers    || {};
  const comm = commissionData?.summary     || {};

  return (
    <div className="space-y-6">
      {/* Period filter */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-gray-600 mr-1">
          <CalendarDays className="h-4 w-4" />
          <span className="text-sm font-medium">Period:</span>
        </div>
        {PERIODS.map((p) => (
          <button
            key={p.value}
            onClick={() => handlePeriodChange(p.value)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              period === p.value
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={refreshOverview}
          disabled={overviewLoading}
          className="ml-auto p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40"
        >
          <RefreshCw className={`h-4 w-4 ${overviewLoading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {overviewLoading ? (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          <span>Loading overview…</span>
        </div>
      ) : overviewError ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <AlertCircle className="h-10 w-10 text-red-400" />
          <p className="text-gray-600">Failed to load overview data.</p>
          <button onClick={refreshOverview} className="theme-button-primary px-4 py-2 rounded-lg text-sm">
            Try Again
          </button>
        </div>
      ) : (
        <>
          {/* Transactions */}
          <div>
            <SectionTitle>Transactions</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard icon={Activity}     label="Total Transactions"  value={num(tx.total)}           iconBg="bg-blue-100"   textColor="text-blue-600"   border="border-blue-100" />
              <KpiCard icon={IndianRupee}  label="Total Revenue"       value={fmt(tx.revenue)}         iconBg="bg-purple-100" textColor="text-purple-600" border="border-purple-100" />
              <KpiCard icon={TrendingUp}   label="Platform Commission" value={fmt(tx.commission)}      sub={`${comm.commission_rate ?? 0}% rate`} iconBg="bg-orange-100" textColor="text-orange-600" border="border-orange-100" />
              <KpiCard icon={CheckCircle}  label="Customer Savings"    value={fmt(tx.customer_savings)} iconBg="bg-green-100"  textColor="text-green-600"  border="border-green-100" />
            </div>
          </div>

          {/* Commission breakdown */}
          <div>
            <SectionTitle>Commission Breakdown</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <KpiCard icon={Percent}     label="Total Transactions"   value={num(comm.total_transactions)}   sub="All businesses" iconBg="bg-blue-100"   textColor="text-blue-600" />
              <KpiCard icon={BadgeCheck}  label="Premium Transactions" value={num(comm.premium_transactions)} iconBg="bg-yellow-100" textColor="text-yellow-600" />
              <KpiCard icon={TrendingUp}  label="Commission Collected" value={fmt(comm.total_commission)}     iconBg="bg-orange-100" textColor="text-orange-600" />
            </div>
          </div>

          {/* Businesses + Customers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <SectionTitle>Businesses</SectionTitle>
              <div className="grid grid-cols-3 gap-3">
                <KpiCard icon={Store}    label="Total"   value={num(biz.total)}   iconBg="bg-gray-100"   textColor="text-gray-700" />
                <KpiCard icon={BadgeCheck} label="Premium" value={num(biz.premium)} iconBg="bg-yellow-100" textColor="text-yellow-600" />
                <KpiCard icon={Building2} label="Basic"   value={num(biz.basic)}   iconBg="bg-blue-50"    textColor="text-blue-500" />
              </div>
            </div>
            <div>
              <SectionTitle>Customers</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <KpiCard icon={Users}    label="Total Customers"  value={num(cust.total)}            iconBg="bg-indigo-100" textColor="text-indigo-600" />
                <KpiCard icon={Activity} label="Active in Period" value={num(cust.active_in_period)} iconBg="bg-teal-100"   textColor="text-teal-600" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Settlements tab ──────────────────────────────────────────────────────────

function SettlementsTab({
  settlementFilters, updateSettlementFilter, handleSettlementPageChange,
  payments, paymentsPagination, paymentsFilter,
  paymentsLoading, paymentsError,
  summary, summaryLoading,
  toggleLoadingMap, handleTogglePayment,
  refreshSettlements,
  MONTHS, YEARS, STATUS_OPTIONS, selectedMonthLabel,
}) {
  const totals = summary?.totals || {};
  const rate   = summary?.collection_rate ?? 0;

  return (
    <div className="space-y-6">
      {/* Filters row */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Year */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Year</label>
            <select
              value={settlementFilters.year}
              onChange={(e) => updateSettlementFilter("year", Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[100px]"
            >
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* Month */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Month</label>
            <select
              value={settlementFilters.month}
              onChange={(e) => updateSettlementFilter("month", Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[140px]"
            >
              {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          {/* Status */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Status</label>
            <select
              value={settlementFilters.status}
              onChange={(e) => updateSettlementFilter("status", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[120px]"
            >
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* Search */}
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Business name or email…"
                value={settlementFilters.search}
                onChange={(e) => updateSettlementFilter("search", e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
          </div>

          <button
            onClick={refreshSettlements}
            disabled={paymentsLoading}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40 self-end"
          >
            <RefreshCw className={`h-4 w-4 ${paymentsLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Summary bar */}
      {summaryLoading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading summary…
        </div>
      ) : summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard icon={Store}       label="Total Businesses"  value={num(totals.total_businesses)}  iconBg="bg-gray-100"   textColor="text-gray-700" />
          <KpiCard icon={BadgeCheck}  label="Paid"              value={num(totals.paid_businesses)}   iconBg="bg-green-100"  textColor="text-green-600" />
          <KpiCard icon={XCircle}     label="Unpaid"            value={num(totals.unpaid_businesses)} iconBg="bg-red-100"    textColor="text-red-600" />
          <KpiCard icon={IndianRupee} label="Amount Collected"  value={fmt(totals.paid_amount)}       iconBg="bg-emerald-100" textColor="text-emerald-600" />
          <KpiCard icon={Clock}       label="Amount Pending"    value={fmt(totals.unpaid_amount)}     iconBg="bg-yellow-100" textColor="text-yellow-600" />
          <KpiCard icon={Percent}     label="Collection Rate"   value={`${rate}%`}                    sub={`of ${fmt(totals.total_amount)}`} iconBg="bg-blue-100" textColor="text-blue-600" />
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            Commission Settlements
            {paymentsFilter && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                — {paymentsFilter.month_name} {paymentsFilter.year}
              </span>
            )}
          </h2>
          {paymentsPagination && (
            <span className="text-sm text-gray-500">{paymentsPagination.total} businesses</span>
          )}
        </div>

        {paymentsLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <span>Loading settlements…</span>
          </div>
        ) : paymentsError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-gray-600">Failed to load settlements.</p>
            <button onClick={refreshSettlements} className="theme-button-primary px-4 py-2 rounded-lg text-sm">
              Try Again
            </button>
          </div>
        ) : payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <BarChart2 className="h-12 w-12 text-gray-300" />
            <p className="text-gray-500 font-medium">
              No commission data for {selectedMonthLabel} {settlementFilters.year}
            </p>
            <p className="text-gray-400 text-sm">Try a different month, year, or status filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Business", "Contact", "Revenue", "Commission", "Paid At", "Status", "Action"].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {payments.map((row, idx) => {
                  const biz        = row.business || {};
                  const fin        = row.financials || {};
                  const settlement = row.settlement;
                  const uuid       = settlement?.uuid;
                  const isPaid     = settlement?.is_paid ?? false;
                  const isToggling = uuid ? (toggleLoadingMap[uuid] ?? false) : false;

                  return (
                    <tr key={uuid || idx} className="hover:bg-gray-50 transition-colors">
                      {/* # */}
                      <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                        {(paymentsPagination
                          ? (paymentsPagination.current_page - 1) * paymentsPagination.per_page
                          : 0) + idx + 1}
                      </td>

                      {/* Business */}
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900 whitespace-nowrap">{biz.business_name}</p>
                        <p className="text-xs text-gray-500">{biz.email}</p>
                      </td>

                      {/* Contact */}
                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {Array.isArray(biz.contact_numbers) && biz.contact_numbers.length > 0
                          ? biz.contact_numbers[0]
                          : "—"}
                      </td>

                      {/* Revenue */}
                      <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                        {fmt(fin.total_revenue)}
                      </td>

                      {/* Commission */}
                      <td className="px-4 py-3 font-bold text-orange-600 whitespace-nowrap">
                        {fmt(fin.total_commission)}
                      </td>

                      {/* Paid at */}
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {fmtDate(settlement?.paid_at)}
                      </td>

                      {/* Status badge */}
                      <td className="px-4 py-3">
                        {settlement ? <PaidBadge isPaid={isPaid} /> : (
                          <span className="text-xs text-gray-400">No record</span>
                        )}
                      </td>

                      {/* Toggle action */}
                      <td className="px-4 py-3">
                        {settlement ? (
                          <button
                            onClick={() => handleTogglePayment(uuid, isPaid)}
                            disabled={isToggling}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              isPaid
                                ? "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                                : "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                            }`}
                          >
                            {isToggling ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : isPaid ? (
                              <XCircle className="h-3.5 w-3.5" />
                            ) : (
                              <CheckCircle className="h-3.5 w-3.5" />
                            )}
                            {isPaid ? "Mark Unpaid" : "Mark Paid"}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {paymentsPagination && paymentsPagination.last_page > 1 && (
          <Pagination
            currentPage={paymentsPagination.current_page}
            totalPages={paymentsPagination.last_page}
            totalItems={paymentsPagination.total}
            itemsPerPage={paymentsPagination.per_page}
            onPageChange={handleSettlementPageChange}
          />
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminReports() {
  const hook = useAdminReport();

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Page header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <BarChart2 className="h-7 w-7 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
            <p className="text-sm text-gray-500">Platform overview and commission settlements</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mt-5 p-1 bg-gray-100 rounded-lg w-fit">
          <TabButton
            label="Overview"
            active={hook.activeTab === TABS.OVERVIEW}
            onClick={() => hook.handleTabChange(TABS.OVERVIEW)}
          />
          <TabButton
            label="Commission Settlements"
            active={hook.activeTab === TABS.SETTLEMENTS}
            onClick={() => hook.handleTabChange(TABS.SETTLEMENTS)}
          />
        </div>
      </div>

      {/* Tab content */}
      {hook.activeTab === TABS.OVERVIEW ? (
        <OverviewTab
          period={hook.period}
          handlePeriodChange={hook.handlePeriodChange}
          dashboardData={hook.dashboardData}
          commissionData={hook.commissionData}
          overviewLoading={hook.overviewLoading}
          overviewError={hook.overviewError}
          refreshOverview={hook.refreshOverview}
          PERIODS={hook.PERIODS}
        />
      ) : (
        <SettlementsTab
          settlementFilters={hook.settlementFilters}
          updateSettlementFilter={hook.updateSettlementFilter}
          handleSettlementPageChange={hook.handleSettlementPageChange}
          payments={hook.payments}
          paymentsPagination={hook.paymentsPagination}
          paymentsFilter={hook.paymentsFilter}
          paymentsLoading={hook.paymentsLoading}
          paymentsError={hook.paymentsError}
          summary={hook.summary}
          summaryLoading={hook.summaryLoading}
          toggleLoadingMap={hook.toggleLoadingMap}
          handleTogglePayment={hook.handleTogglePayment}
          refreshSettlements={hook.refreshSettlements}
          MONTHS={hook.MONTHS}
          YEARS={hook.YEARS}
          STATUS_OPTIONS={hook.STATUS_OPTIONS}
          selectedMonthLabel={hook.selectedMonthLabel}
        />
      )}
    </div>
  );
}
